import type { ReactElement } from "react";
import { Link } from "react-router";

import { useApiData } from "../../api/use-api-data.js";
import { usePageMeta } from "../../app/use-page-meta.js";
import { ApiState, titleFor } from "../../components/api-state.js";
import { useRowLink } from "../../components/row-link.js";

import { invoiceApi, invoiceUrls } from "./urls.js";

/** 請求書を見られる契約 1 行（<c>GET /api/invoices</c>。サーバの ApiInvoiceMenuRow と同じ欄）。 */
export interface InvoiceMenuRow {
    readonly tenantId: number;
    readonly title: string;
    /** 請求書の宛名（上書きがあればそれ、無ければ企業名）。 */
    readonly addressee: string;
}

export interface InvoiceMenu {
    readonly tenants: readonly InvoiceMenuRow[];
}

/**
 * 請求書のメニュー（<c>/TenantInvoice/Menu</c>）。<b>契約の画面を通らずに請求書を開く入口</b>（入口は契約一覧の「請求書」）。
 * 並べる契約はサーバが決める（利用者として見られる契約と、利用者でなく閲覧者に書かれた契約）。
 * 行を押すと、その契約の請求書の一覧を <c>from=menu</c> で開く（一覧の「〜へ戻る」がここを向く）。
 */
export function InvoiceMenuPage(): ReactElement | null {
    const data = useApiData<InvoiceMenu>(invoiceApi.menu);
    usePageMeta(titleFor(data, "請求書のメニュー"));
    const rowLink = useRowLink();

    if (data.state !== "ready") {
        return <ApiState data={data} what="請求書のメニュー" />;
    }
    const menu = data.value;

    return (
        <div className="page max-w-6xl">
            <p className="mb-4"><Link to={invoiceUrls.tenants} className="btn-secondary btn-sm">契約一覧へ戻る</Link></p>
            <div className="page-header">
                <div>
                    <h1 className="page-title">請求書のメニュー</h1>
                    <p className="page-subtitle">請求書を見られる契約の一覧です。契約を選ぶと、その契約の請求書の一覧を開きます。</p>
                </div>
            </div>

            {menu.tenants.length === 0
                ? (
                    <div className="empty-state">
                        <p className="text-slate-500">請求書を見られる契約はありません。</p>
                    </div>
                )
                : (
                    <div className="table-card">
                        <div className="table-scroll">
                            <table className="data-table data-table-stack">
                                <thead>
                                    <tr>
                                        <th scope="col">システム名</th>
                                        <th scope="col">請求書の宛名</th>
                                        <th scope="col"></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {menu.tenants.map((tenant) => {
                                        const href = invoiceUrls.listFromMenu(tenant.tenantId);
                                        return (
                                            // 行ごと押せる。**「請求書」は残す**——キーボードと読み上げはそちらを辿る
                                            <tr key={tenant.tenantId} data-invoice-tenant="" data-row-href={href} onClick={rowLink(href)}>
                                                <td data-label="システム名" className="break-words font-semibold text-slate-900">{tenant.title}</td>
                                                <td data-label="請求書の宛名" className="break-words">{tenant.addressee}</td>
                                                <td className="cell-actions">
                                                    <Link className="btn-secondary btn-sm" to={href}>請求書</Link>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
        </div>
    );
}
